/**
 * TeamThemeProvider
 * -----------------
 * Writes the current team code to `document.documentElement`'s
 * `data-team` attribute so [data-team="..."] CSS blocks in
 * design-tokens.css take effect.
 *
 * Also exposes the current team via React context (`useTeamTheme()`) so
 * descendant components can read it without prop-drilling.
 * Named `useTeamTheme` (not `useTeam`) to avoid collision with the
 * unrelated `useTeam` hook in `src/lib/teamConfig`.
 *
 * Valid team codes MUST match a [data-team="<code>"] selector in
 * src/styles/design-tokens.css. Unknown codes warn in dev and fall
 * through to the :root default team-primary (Orioles orange).
 */

import React, { createContext, useContext, useEffect } from 'react';
import { ALL_SLUGS } from '../../lib/mlb/teamCodes';

export const VALID_TEAMS = Object.freeze(ALL_SLUGS);

const TeamThemeContext = createContext(null);

function isValidTeam(team) {
  return typeof team === 'string' && VALID_TEAMS.includes(team);
}

export function TeamThemeProvider({ team, children }) {
  if (process.env.NODE_ENV !== 'production' && !isValidTeam(team)) {
    // Warn only — never throw. Prod degrades silently to :root default.
    // eslint-disable-next-line no-console
    console.warn(
      `[TeamThemeProvider] Unknown team code "${team}". ` +
        `Expected one of: ${VALID_TEAMS.join(', ')}.`
    );
  }

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const root = document.documentElement;
    root.setAttribute('data-team', team);
    return () => {
      // Only clear if nothing else has changed it since we set it.
      if (root.getAttribute('data-team') === team) {
        root.removeAttribute('data-team');
      }
    };
  }, [team]);

  return (
    <TeamThemeContext.Provider value={team}>
      {children}
    </TeamThemeContext.Provider>
  );
}

export function useTeamTheme() {
  return useContext(TeamThemeContext);
}
